import React, { useState, useEffect } from "react";
import {
  PageHeader, Alert, Btn, FormField, ContentSection,
  inputStyle, selectStyle,
  colors, spacing, typography, radius, shadows,
} from "../design-system";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const API = `${BASE}/accreditation-rules`;

// ── Local helpers ─────────────────────────────────────────────────────────────

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: typography.fontSizes.xs,
      fontWeight: typography.fontWeights.semibold,
      color: colors.textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      paddingBottom: spacing.xs,
      borderBottom: `1px solid ${colors.border}`,
      marginBottom: spacing.md,
      marginTop: spacing.lg,
    }}>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        background: checked ? colors.primary : colors.border,
        position: "relative",
        transition: "background 150ms ease",
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        position: "absolute",
        top: 3,
        left: checked ? 23 : 3,
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "#fff",
        transition: "left 150ms ease",
        boxShadow: shadows.xs,
      }} />
    </button>
  );
}

function ToggleRow({ label, description, field, value, onChange, disabled }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.md,
      padding: `${spacing.sm} 0`,
      borderBottom: `1px solid ${colors.border}`,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.medium, color: colors.textPrimary }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>
            {description}
          </div>
        )}
      </div>
      <Toggle
        checked={!!value}
        onChange={(v) => onChange(field, v)}
        disabled={disabled}
      />
    </div>
  );
}

function NumberField({ label, description, field, value, onChange, min = 1, disabled }) {
  return (
    <FormField label={label} hint={description}>
      <input
        type="number"
        min={min}
        value={value ?? ""}
        onChange={e => onChange(field, e.target.value === "" ? null : parseInt(e.target.value, 10))}
        disabled={disabled}
        style={{ ...inputStyle, width: 120 }}
      />
    </FormField>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AccreditationRules() {
  const [config, setConfig]   = useState(null);
  const [form, setForm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState("");
  const [msgType, setMsgType] = useState("success");
  const [dirty, setDirty]     = useState(false);

  const token    = localStorage.getItem("token");
  const userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
  const isSuperAdmin = userRole === "super_admin";
  const headers  = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const flash = (m, t = "success") => {
    setMsg(m); setMsgType(t);
    setTimeout(() => setMsg(""), 4000);
  };

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/`, { headers })
      .then(r => r.json())
      .then(d => { setConfig(d); setForm({ ...d }); })
      .catch(() => flash("Failed to load accreditation rules.", "error"))
      .finally(() => setLoading(false));
  }, []);

  function handleChange(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || "Save failed");
      }
      const updated = await res.json();
      setConfig(updated);
      setForm({ ...updated });
      setDirty(false);
      flash("Accreditation rules saved successfully.");
    } catch (err) {
      flash("Error: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setForm({ ...config });
    setDirty(false);
  }

  if (loading) {
    return (
      <div>
        <PageHeader title="Accreditation Rules" subtitle="Loading..." />
        <div style={{ textAlign: "center", padding: spacing.xl, color: colors.textMuted }}>
          Loading configuration…
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div>
        <PageHeader title="Accreditation Rules" />
        <Alert type="error" message="Failed to load accreditation rules configuration." />
      </div>
    );
  }

  const readOnly = !isSuperAdmin;

  return (
    <div>
      <PageHeader
        title="Accreditation Rules"
        subtitle="Global enforcement settings for the ICSC accreditation lifecycle."
        actions={isSuperAdmin && (
          <div style={{ display: "flex", gap: spacing.sm }}>
            {dirty && <Btn variant="secondary" onClick={handleReset}>Reset</Btn>}
            <Btn onClick={handleSave} loading={saving} disabled={!dirty}>
              Save Rules
            </Btn>
          </div>
        )}
      />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {readOnly && (
        <Alert
          type="info"
          message="You are viewing the accreditation rules in read-only mode. SuperAdmin access is required to edit."
        />
      )}

      <form onSubmit={handleSave}>
        {/* ── Lifecycle Durations ─────────────────────────────────────────── */}
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.lg,
          padding: spacing.lg,
          marginBottom: spacing.lg,
          boxShadow: shadows.cardShadow,
        }}>
          <SectionTitle>Lifecycle Durations</SectionTitle>
          <div style={{ display: "flex", gap: spacing.xl, flexWrap: "wrap" }}>
            <NumberField
              label="Accreditation Duration (months)"
              description="Default length of new accreditations"
              field="default_accreditation_duration_months"
              value={form.default_accreditation_duration_months}
              onChange={handleChange}
              disabled={readOnly}
            />
            <NumberField
              label="Surveillance Frequency (months)"
              description="How often surveillance audits are scheduled"
              field="surveillance_frequency_months"
              value={form.surveillance_frequency_months}
              onChange={handleChange}
              disabled={readOnly}
            />
            <NumberField
              label="Renewal Window (days)"
              description="Days before expiry when renewal window opens"
              field="renewal_window_days"
              value={form.renewal_window_days}
              onChange={handleChange}
              disabled={readOnly}
            />
          </div>
        </div>

        {/* ── Evidence & Scope ────────────────────────────────────────────── */}
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.lg,
          padding: spacing.lg,
          marginBottom: spacing.lg,
          boxShadow: shadows.cardShadow,
        }}>
          <SectionTitle>Evidence &amp; Scope Requirements</SectionTitle>
          <ToggleRow
            label="Evidence Required"
            description="Accreditation applications must include supporting evidence documents"
            field="evidence_required"
            value={form.evidence_required}
            onChange={handleChange}
            disabled={readOnly}
          />
          <ToggleRow
            label="Scope Evidence Required"
            description="Evidence must be uploaded to each scope before approval is granted"
            field="scope_evidence_required"
            value={form.scope_evidence_required}
            onChange={handleChange}
            disabled={readOnly}
          />
          <ToggleRow
            label="Auto-Expire Overdue Accreditations"
            description="Automatically expire accreditations and renewals that have passed their due date"
            field="auto_expire"
            value={form.auto_expire}
            onChange={handleChange}
            disabled={readOnly}
          />
        </div>

        {/* ── Certificate Issuance Gates ──────────────────────────────────── */}
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.lg,
          padding: spacing.lg,
          marginBottom: spacing.lg,
          boxShadow: shadows.cardShadow,
        }}>
          <SectionTitle>Certificate Issuance Gates</SectionTitle>
          <p style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
            When enabled, these checks are enforced before an accreditation certificate can be generated.
            Any failed check returns a 403 with an actionable error message.
          </p>
          <ToggleRow
            label="Require Accreditation Scope"
            description="CB must have at least one active approved scope before certificate issuance"
            field="certificate_issue_requires_scope"
            value={form.certificate_issue_requires_scope}
            onChange={handleChange}
            disabled={readOnly}
          />
          <ToggleRow
            label="Require Active Billing"
            description="CB must have an active membership/billing status"
            field="certificate_issue_requires_billing"
            value={form.certificate_issue_requires_billing}
            onChange={handleChange}
            disabled={readOnly}
          />
          <ToggleRow
            label="Require Accredited Auditor (Auditor certs only)"
            description="Auditor must have an approved accreditation record before a certificate is issued"
            field="certificate_issue_requires_accredited_auditor"
            value={form.certificate_issue_requires_accredited_auditor}
            onChange={handleChange}
            disabled={readOnly}
          />
          <ToggleRow
            label="Block on Overdue Surveillance"
            description="Certificate issuance is blocked if the CB has any overdue surveillance audits"
            field="certificate_issue_requires_no_overdue_surveillance"
            value={form.certificate_issue_requires_no_overdue_surveillance}
            onChange={handleChange}
            disabled={readOnly}
          />
          <ToggleRow
            label="Block on Unresolved Major NCs"
            description="Certificate issuance is blocked if any surveillance audit has unresolved major nonconformities"
            field="certificate_issue_requires_no_major_nc"
            value={form.certificate_issue_requires_no_major_nc}
            onChange={handleChange}
            disabled={readOnly}
          />
        </div>

        {/* ── Auditor Requirements ────────────────────────────────────────── */}
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.lg,
          padding: spacing.lg,
          marginBottom: spacing.lg,
          boxShadow: shadows.cardShadow,
        }}>
          <SectionTitle>Auditor Requirements</SectionTitle>
          <div style={{ display: "flex", gap: spacing.xl, flexWrap: "wrap" }}>
            <FormField
              label="Minimum Qualifications"
              hint="Required qualification level (e.g. 'ISO 9001 Lead Auditor'). Leave blank to not enforce."
              style={{ flex: 1, minWidth: 260 }}
            >
              <input
                type="text"
                value={form.auditor_min_qualifications ?? ""}
                onChange={e => handleChange("auditor_min_qualifications", e.target.value || null)}
                placeholder="e.g. ISO 9001 Lead Auditor"
                disabled={readOnly}
                style={inputStyle}
              />
            </FormField>
            <NumberField
              label="Minimum Experience (years)"
              description="Required years of auditing experience. Leave blank to not enforce."
              field="auditor_min_experience_years"
              value={form.auditor_min_experience_years}
              onChange={handleChange}
              min={0}
              disabled={readOnly}
            />
          </div>
        </div>

        {/* ── Save footer ─────────────────────────────────────────────────── */}
        {isSuperAdmin && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing.sm }}>
            {dirty && <Btn variant="secondary" onClick={handleReset}>Reset Changes</Btn>}
            <Btn type="submit" loading={saving} disabled={!dirty}>
              Save Rules
            </Btn>
          </div>
        )}
      </form>

      {/* ── Last updated ────────────────────────────────────────────────── */}
      {config?.updated_at && (
        <p style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, textAlign: "right", marginTop: spacing.md }}>
          Last updated: {new Date(config.updated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
